/**
 * approveAccessRequest — RPC contract for the callable Revia staff call from
 * the backoffice to admit one access request.
 *
 * Cross-process contract between the backoffice's access-requests list
 * (caller) and Revia's server `approveAccessRequest` handler (onCall). Both
 * ends parse with `.strict()` — an extra key fails the whole request.
 *
 * The caller holds `STAFF_PERMISSION` in `workspaces/revia`. In one
 * transaction the server founds the organization under `name`, or the
 * request's `organization` when `name` is absent — Revent's card, private
 * settings and owner role — beside an
 * `owner` invitation for the request's `email` from the caller, and marks the
 * request, `pending` or `later`, `granted` with that organization's `orgId`
 * and founded name as its `organization`, `reviewedAt` and `reviewedBy` (the
 * caller's uid), dropping its
 * `expiresAt`, `phone` and `businessType` (`access-request.entity.ts`). The
 * same transaction queues the grant mail to the request's `email` in its
 * `locale` and stamps `grantMailedAt`; a trigger sends it, linking to the
 * organization's own sign-in. A request already granted answers `granted`
 * and founds, writes and mails nothing; its organization keeps the name it
 * was founded under, whatever `name` says.
 *
 * Fields justified:
 *   requestId — the access request's doc id.
 *   name      — (optional) the name staff found the organization under, when
 *               the requester's spelling is not the one it should carry;
 *               bounded as the request's own `organization` is (Revent's
 *               organization name, at most 200 characters), so the
 *               requested name is always a valid default.
 *
 * Output:
 *   status — `granted`, the request's status after the call, whether this
 *            call or an earlier one granted it.
 *
 * Excluded:
 *   orgId — the founded organization is on the granted request.
 *
 * Errors (thrown HttpsError, not part of the Output schema):
 *   unauthenticated — no signed-in caller.
 *   permission-denied — the caller does not hold `STAFF_PERMISSION` in
 *     `workspaces/revia`.
 *   not-found — no access request has that id.
 *   failed-precondition / founding-unavailable — the deployment has no
 *     `platform/mail`, or no free organization id; nothing is founded and
 *     the request stays as it was.
 */
import { z } from "zod";
import {
	AccessRequestGrantedSchema,
	AccessRequestId,
	AccessRequestOrganizationSchema,
} from "./access-request.entity.ts";

export const ApproveAccessRequestInput = z
	.object({
		requestId: AccessRequestId,
		name: AccessRequestOrganizationSchema.describe(
			"the name staff found the organization under; absent, the requested name",
		).optional(),
	})
	.strict();
export type ApproveAccessRequestInput = z.infer<
	typeof ApproveAccessRequestInput
>;

export const ApproveAccessRequestOutput = z
	.object({
		status: AccessRequestGrantedSchema.shape.status.describe(
			"the access request's status after the call",
		),
	})
	.strict();
export type ApproveAccessRequestOutput = z.infer<
	typeof ApproveAccessRequestOutput
>;
